import {
  CompetitionLevel,
  Organization,
  postOrganizationsByOrgIdDeleteMutation,
  SportType,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuProps,
  Icons,
  toast,
  useConfirmStore,
} from "@win-reality/react-ui";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usaStates } from "typed-usa-states";

import AccountEditModal from "@/components/account-edit-modal";
import { competitionLevelsOptions } from "@/lib/competition-level-options";
import { useManageLayoutContext } from "@/pages/Manage/ManageLayout";
import { delay } from "@/utils/delay";

const useDropdownMenuItems = ({
  orgId,
  setIsEditModalOpen,
  setIsDropdownMenuOpen,
}: {
  orgId: string;
  setIsEditModalOpen: (value: boolean) => void;
  setIsDropdownMenuOpen: (value: boolean) => void;
}) => {
  const navigate = useNavigate();
  const { setOrgId } = useManageLayoutContext();

  const queryClient = useQueryClient();

  const deleteOrganization = useMutation({
    ...postOrganizationsByOrgIdDeleteMutation(),
    onError: () => {
      toast("Failed to delete organization", { variant: "error" });
    },
  });

  const confirm = useConfirmStore((state) => state.confirm);

  const handleDelete = useCallback(async () => {
    const confirmed = await confirm({
      title: "Confirm Organization Deletion",
      description: "Are you sure you want to remove this organization?",
      confirmText: "Delete Organization",
      confirmButtonVariant: "destructive",
    });

    if (confirmed) {
      useConfirmStore.setState({ isPending: true });

      await deleteOrganization.mutateAsync(
        {
          path: {
            org_id: encodeURIComponent(orgId!) ?? "",
          },
        },
        {
          onSettled: () => {
            useConfirmStore.setState({ isOpen: false, isPending: false });
          },
          onSuccess: async () => {
            toast("Organization removed", {
              variant: "success",
            });

            await delay(1000);

            queryClient.invalidateQueries({
              queryKey: ["getOrganizationsByOrgIdPlayers", orgId],
            });
          },
        },
      );
    }
  }, [confirm, deleteOrganization, orgId, queryClient]);

  const dropdownMenuItems = useMemo(() => {
    const items: DropdownMenuProps["items"] = [
      {
        label: "Operators",
        onClick: (e) => {
          e.stopPropagation();
          e.preventDefault();
          setOrgId(orgId);
          navigate(`/manage/operators`);
          setIsDropdownMenuOpen(false);
        },
      },
      {
        label: "Rosters",
        onClick: (e) => {
          e.stopPropagation();
          e.preventDefault();
          setOrgId(orgId);
          navigate(`/manage/rosters`);
          setIsDropdownMenuOpen(false);
        },
      },
      {
        label: "Players",
        onClick: (e) => {
          e.stopPropagation();
          e.preventDefault();
          setOrgId(orgId);
          navigate(`/manage/players`);
          setIsDropdownMenuOpen(false);
        },
      },
      {
        label: "Edit",
        onClick: (e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsEditModalOpen(true);
          setIsDropdownMenuOpen(false);
        },
      },
      <DropdownMenuItem
        onClick={handleDelete}
        key="delete-organization"
        className="light:text-error"
      >
        Delete Organization
      </DropdownMenuItem>,
    ];

    return items;
  }, [
    handleDelete,
    setOrgId,
    orgId,
    navigate,
    setIsDropdownMenuOpen,
    setIsEditModalOpen,
  ]);

  return { dropdownMenuItems };
};

const ActionsMenu = ({ account }: { account: Organization }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { dropdownMenuItems } = useDropdownMenuItems({
    orgId: account.orgId,
    setIsEditModalOpen,
    setIsDropdownMenuOpen: setIsOpen,
  });

  return (
    <>
      <DropdownMenu
        variant="light"
        open={isOpen}
        onOpenChange={setIsOpen}
        align="end"
        items={dropdownMenuItems}
        onClick={(e) => e.stopPropagation()}
        trigger={
          <Button
            variant="ghost"
            size="icon"
            className={cn(isOpen && "bg-[hsla(218,100%,52%,0.1)]")}
          >
            <Icons.EllipsisVertical className="text-blue" />
          </Button>
        }
      />

      <AccountEditModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        defaultValues={{
          accountName: account.orgName,
          competitionLevel:
            account.competitionLevel ?? ("" as CompetitionLevel),
          sport: account.sport ?? ("" as SportType),
          city: account.city ?? "",
          state: account.state ?? "",
          teamType: "CSB",
        }}
        organizationId={account.orgId}
      />
    </>
  );
};

export const useColumns = () => {
  const columns: ColumnDef<Organization>[] = useMemo(
    () => [
      {
        accessorKey: "orgName",
        header: "Name",
        size: 200,
      },
      {
        accessorKey: "competitionLevel",
        size: 175,

        header: "Comp. Level",
        cell: ({ row: { original: row } }) => {
          const label =
            competitionLevelsOptions.find(
              (option) => option.value === row?.competitionLevel,
            )?.label || row?.competitionLevel;
          return <span>{label}</span>;
        },
      },
      {
        accessorKey: "city",
        header: "City",
        size: 100,
      },
      {
        accessorKey: "state",
        header: "State",
        size: 100,

        cell: ({ row: { original: row } }) => {
          const state = usaStates.find(
            (state) => state.abbreviation === row?.state,
          );
          return <span>{state ? state?.name : ""}</span>;
        },
      },
      {
        accessorKey: "sport",
        header: "Sport",
        size: 100,
        cell: ({ row: { original: row } }) => {
          return <span className="capitalize">{row?.sport}</span>;
        },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row: { original: row } }) => <ActionsMenu account={row} />,
      },
    ],
    [],
  );

  return { columns };
};
